import { memo, useCallback, useMemo, useState } from 'react';
import { doCheckArray } from '@shared/helpers/index.ts';
import { AsideFilter } from '@shared/ui/aside-filter';
import { CheckboxList } from '@shared/ui/aside-filter/checkbox-list';
import { CountOfSelected } from '@shared/ui/aside-filter/count-of-selected/index.ts';
import { AuthorList } from '@shared/ui/Filter/AuthorList/index.tsx';
import { PartitionsList } from '@shared/ui/Filter/SectionList/index.tsx';
import type { AccordionPT } from '@shared/ui/layout/accordion/ui.tsx';
import { Partition, Tag } from '@src/api/common.ts';
import { AuthorFavorite } from '@src/api/users.ts';
import * as st from './styles.module.scss';

export type ProgramListFilterProps = {
  partitions: Partition[];
  tags: Tag[];
  authors?: AuthorFavorite[];
  isOpen: boolean;
  selectedTags: number[];
  onTagChange: (tags: number[]) => void;
  selectedAuthors: number[];
  onAuthorChange: (authors: number[]) => void;
  className?: string;
  hasResetButton: boolean;
  handleResetFilter: () => void;
  activeTab: string;
  onStateChange?: () => void;
  selectedPartitions?: number[];
  onPartitionChange?: (partitionId: number) => void;
};

const updateSelectionList = (
  currentList: number[],
  updateFunction: (updatedList: number[]) => void,
  id: number
): void => {
  const updatedList = currentList.includes(id)
    ? currentList.filter((item) => item !== id)
    : [...currentList, id];
  updateFunction(updatedList);
};

export const ProgramListFilter = memo(
  ({
    isOpen,
    selectedTags,
    onTagChange,
    selectedAuthors,
    onAuthorChange,
    hasResetButton,
    handleResetFilter,
    onStateChange,
    activeTab,
    selectedPartitions,
    onPartitionChange,
    partitions,
    tags,
    authors,
  }: ProgramListFilterProps): JSX.Element => {
    const [isTagsOpen, setIsTagsOpen] = useState(false);
    const handleTagClick = useCallback(
      (id: number): void => {
        updateSelectionList(selectedTags, onTagChange, id);
      },
      [selectedTags, onTagChange]
    );

    const handleAuthorClick = useCallback(
      (id: number): void => {
        updateSelectionList(selectedAuthors, onAuthorChange, id);
      },
      [selectedAuthors, onAuthorChange]
    );

    const sectionList = useMemo(() => {
      const output = [
        {
          title: 'РАЗДЕЛЫ',
          children: (
            <PartitionsList
              // icon={<VideosIcon />}
              partitions={partitions}
              selectedPartitionId={selectedPartitions?.at(0)}
              onPartitionClick={(partitionId) => {
                onStateChange && onStateChange();
                onTagChange([]);
                onPartitionChange?.(partitionId);
              }}
            />
          ),
          isAlwaysOpen: true,
        },
      ] as AccordionPT[];

      doCheckArray(tags) &&
        output.push({
          title: 'ТЕГИ',
          headerChildrenOnClose: <CountOfSelected count={selectedTags.length} />,
          children: (
            <CheckboxList
              items={tags}
              onItemClick={(id) => {
                handleTagClick(id);
              }}
              selectedItems={selectedTags}
            />
          ),
          onOpen: () => setIsTagsOpen(true),
          onClose: () => setIsTagsOpen(false),
          isOpenInit: Boolean(doCheckArray(selectedTags)) || isTagsOpen,
        });

      doCheckArray(authors) &&
        activeTab !== 'my' &&
        output.push({
          title: 'АВТОР',
          headerChildrenOnClose: <CountOfSelected count={selectedAuthors.length} />,
          children: (
            <AuthorList
              authors={authors}
              onAuthorClick={handleAuthorClick}
              selectedAuthors={selectedAuthors}
            />
          ),
          isOpenInit: Boolean(doCheckArray(selectedAuthors)),
        });

      return output;
    }, [
      isTagsOpen,
      onStateChange,
      onTagChange,
      selectedPartitions,
      onPartitionChange,
      partitions,
      activeTab,
      authors,
      handleAuthorClick,
      handleTagClick,
      selectedAuthors,
      selectedTags,
      tags,
    ]);

    return (
      <AsideFilter
        classNameOnOpen={st.on_open}
        onResetFilter={handleResetFilter}
        showResetBtn={hasResetButton}
        isOpen={isOpen}
        sections={sectionList}
      />
    );
  }
);
